import { eFinSuiteAdminMessages } from 'src/constants/AppConstants';
import { FormikErrors } from 'formik';
import * as Yup from 'yup';
import { isValidUrlNameFormat } from 'src/utilities/urlName';
import { IS_NON_PROD } from 'src/constants/EnvironmentConstants';
import { ALLOWED_LOCALHOST_ORIGINS } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';

export const APP_NAME_MAX_LENGTH = 30;

export const URL_NAME_FORMAT_ERROR =
  'App-specific Path must be lowercase letters and numbers separated by a single - or _ (e.g. prophecy-arc). No spaces, uppercase, or other symbols.';

/**
 * Stable, non-user-facing identifier for the URL-scheme test.
 *
 * SECURITY / UX: this constant — NOT `eFinSuiteAdminMessages.VALID_URL` — is the
 * source of truth the submit handler uses to decide whether to surface the
 * link-specific step error. Every Yup test that enforces the https-only rule
 * MUST register itself under this exact test name so that a downstream check
 * on `error.inner[].type` (via `collectFailedYupTests`) can identify a URL-scheme
 * failure regardless of the human-readable message copy.
 *
 * If you edit `eFinSuiteAdminMessages.VALID_URL` (localization, tone change,
 * etc.) the control flow keeps working. If you edit *this* constant, update
 * the branch selector in ApplicationFormUtilities.tsx and its tests.
 */
export const HTTPS_URL_TEST_NAME = 'https-url';

/**
 * Walk a Yup / Formik validation-error tree (nested objects + arrays of objects
 * + arrays of strings) and return every leaf error message as a flat string[].
 *
 * NOTE: kept for backwards-compatibility and for tests that inspect message
 * copy. New control-flow code SHOULD use `collectFailedYupTests` instead —
 * matching on test *names* is not coupled to display strings and survives
 * localization / wording changes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const collectYupErrorMessages = (errors: FormikErrors<any> | Record<string, unknown> | undefined | null): string[] => {
  if (!errors) return [];
  const out: string[] = [];
  const visit = (node: unknown): void => {
    if (node === null || node === undefined) return;
    if (typeof node === 'string') {
      out.push(node);
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node === 'object') {
      Object.values(node as Record<string, unknown>).forEach(visit);
    }
  };
  visit(errors);
  return out;
};

export const SHORT_DESCRIPTION_MAX_LENGTH = 60;
export const FULL_DESCRIPTION_MAX_LENGTH = 600;

export const WELCOME_TITLE_MAX_LENGTH = 21;
export const WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH = 46;
const WELCOME_LINK_DESCRIPTION_MAX_LENGTH = 1000;

export const OPTIONAL_MULTI_SELECT_DROPDOWN = Yup.array().of(
  Yup.object().shape({
    label: Yup.string(),
    id: Yup.string()
  })
);

export const REQUIRED_MULTI_SELECT_DROPDOWN = Yup.array()
  .of(
    Yup.object().shape({
      label: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
      id: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD)
    })
  )
  .min(1, eFinSuiteAdminMessages.AT_LEAST_ONE_SELECTION)
  .required(eFinSuiteAdminMessages.REQUIRED_FIELD);

export const REQUIRED_SELECT_DROPDOWN = Yup.object().shape({
  label: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  id: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD)
});

/**
 * SECURITY: A URL is only considered valid for FinSuite application configuration
 * if it parses as an absolute URL AND uses the `https:` scheme.
 *
 * Yup's built-in `.url()` accepts dangerous schemes such as `javascript:` and
 * `data:`, which an attacker can weaponize into a stored-XSS payload once the
 * URL reaches the embedded-app iframe (see IFrameComponent.tsx) or a link
 * element with `href={applicationLink}`. Rejecting non-https schemes here
 * blocks the payload at the admin form boundary; server-side validation in
 * PR 2 adds the authoritative check.
 *
 * NON-PROD ESCAPE HATCH: in non-production stages only (dev/alpha), the explicit
 * http localhost origins in `ALLOWED_LOCALHOST_ORIGINS` (xPT :4444, Verity :4555)
 * are also accepted so admins can configure local dev servers as embed targets.
 * This mirrors the same carve-out already present in the embed-render sanitizer
 * (`sanitizeEmbeddingUrl` in EmbeddingUtils.ts) and the backend `validate_url`
 * (`allow_localhost`), keeping all three layers in agreement. The list is
 * port-exact and http-only; anything else on localhost is still rejected. This
 * exception NEVER applies in beta/prod because `IS_NON_PROD` is false there.
 */
const isValidHttpsUrl = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined || value === '') return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  // Non-prod escape hatch: explicit http localhost origins for embed testing.
  if (IS_NON_PROD && ALLOWED_LOCALHOST_ORIGINS.has(parsed.origin)) return true;
  return parsed.protocol === 'https:';
};

// Convenience Yup test factory so every URL field gets the same rule.
// All URL-scheme tests MUST use `HTTPS_URL_TEST_NAME` as their `name` so
// downstream control flow can identify them without matching the message copy.
const httpsUrlTest = (message: string) => ({
  name: HTTPS_URL_TEST_NAME,
  message,
  test: (value: string | null | undefined) => {
    // Empty/nullable is handled by upstream `.required()`/`.nullable()` — only
    // fail if a non-empty value was provided that does not parse as https:.
    if (value === null || value === undefined || value === '') return true;
    return isValidHttpsUrl(value);
  }
});

export const nonProductionEnvironmentSchema = Yup.object().shape({
  id: Yup.mixed().nullable(),
  displayText: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  // SECURITY: https-only. Blocks stored-XSS via `javascript:` in non-prod env links.
  // NOTE: uses the shared `httpsUrlTest(...)` factory so this failure is picked
  // up by `collectFailedYupTests` under HTTPS_URL_TEST_NAME just like every
  // other https-only URL field on the form. The factory treats empty/nullable
  // as passing so `.required()` owns the empty case — otherwise an empty link
  // would fail BOTH `.required()` and the https test, and (because Formik runs
  // Yup with `abortEarly: false`) `collectFailedYupTests` would surface the
  // https-specific message for a link that is merely missing.
  link: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD).test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL))
});

export const shortDescriptionRegexPattern = new RegExp(`^(?=.{1,${SHORT_DESCRIPTION_MAX_LENGTH}}$).*$`);
export const SHORT_DESCRIPTION_ERROR_MESSAGE = `Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters`;

export const logoConfigSchema = Yup.object()
  .nullable()
  .default(null)
  .shape({
    logoType: Yup.string().oneOf(['bundled', 'shared-library', 'app-specific']).required(),
    selectedLogoName: Yup.string().nullable(),
    uploadedFileName: Yup.string()
      .nullable()
      .when('logoType', {
        is: 'app-specific',
        then: Yup.string().nullable().required('Upload a logo file.'),
        otherwise: Yup.string().nullable()
      }),
    s3Location: Yup.object()
      .nullable()
      .when('logoType', {
        is: 'app-specific',
        then: Yup.object().nullable().required('Logo upload failed or is still in progress.').shape({
          bucket: Yup.string().required(),
          key: Yup.string().required(),
          region: Yup.string().required(),
          contentType: Yup.string().required()
        }),
        otherwise: Yup.object().nullable()
      })
  });

export const applicationDetailsSchema = (existingApplicationNames: string[], shouldSkipAppNameUniquenessValidation: boolean) => {
  return Yup.object().shape({
    applicationName: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .max(APP_NAME_MAX_LENGTH, `Max ${APP_NAME_MAX_LENGTH} characters`)
      .test('unique-application-name', eFinSuiteAdminMessages.UNIQUE_APP_NAME_ERROR, async function (value) {
        if (shouldSkipAppNameUniquenessValidation) {
          return true;
        }
        if (existingApplicationNames && existingApplicationNames.includes(value || '')) {
          return false;
        }
        return true;
      }),
    // App-specific Path (urlName): optional on the form — when empty the backend derives
    // it from applicationName. When present it must be a well-formed, non-reserved
    // slug. Uniqueness is enforced authoritatively by the backend resolver.
    urlName: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .test('url-name-format', URL_NAME_FORMAT_ERROR, (value) => (value ? isValidUrlNameFormat(value) : true)),
    // SECURITY: https-only. `Yup.string().url()` alone accepts `javascript:` /
    // `data:` URLs, which an attacker could store and later have rendered into
    // an iframe `src`. Enforce https here at the admin form boundary.
    applicationLink: Yup.string().test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL)),
    applicationOwnerLabelInput: REQUIRED_SELECT_DROPDOWN,
    shortApplicationDescription: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .matches(shortDescriptionRegexPattern, `Max ${SHORT_DESCRIPTION_MAX_LENGTH} characters`),
    fullApplicationDescription: Yup.string()
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
      .max(FULL_DESCRIPTION_MAX_LENGTH, `Max ${FULL_DESCRIPTION_MAX_LENGTH} characters`),
    glAccounts: REQUIRED_SELECT_DROPDOWN,
    toolUI: REQUIRED_SELECT_DROPDOWN,
    qChatConfig: Yup.object()
      .nullable()
      .default(null)
      .shape({
        // SECURITY: https-only for the Q Chat agent URL — see rationale above.
        agentUrl: Yup.string()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .nullable()
          .when('isEnabled', {
            is: true,
            then: Yup.string()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .nullable()
              .required('Agent URL is required when Q Chat is enabled.')
              .test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL)),
            otherwise: Yup.string()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .nullable()
              .test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL))
          }),
        isEnabled: Yup.boolean().nullable()
      }),
    logoConfig: logoConfigSchema
  });
};

export const reportIssuesAndAdditionalDetailsSchema = Yup.object().shape({
  id: Yup.mixed().nullable(),
  position: Yup.number()
    .typeError(eFinSuiteAdminMessages.ENTER_VALID_NUMBER)
    .nullable()
    .min(1, 'Value must be between 1 and 8')
    .max(8, 'Value must be between 1 and 8'),
  displayName: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  // SECURITY: https-only for report-issue / additional-detail links.
  link: Yup.string().required(eFinSuiteAdminMessages.REQUIRED_FIELD).test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL))
});

export const welcomeContentDetailsSchemaForApplications = Yup.object().shape({
  id: Yup.mixed().nullable(),
  displayText: Yup.string()
    .nullable()
    .required(eFinSuiteAdminMessages.REQUIRED_FIELD)
    .max(WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH, `Max ${WELCOME_LINK_DISPLAY_NAME_MAX_LENGTH} characters`),
  // SECURITY: https-only for welcome-content links.
  link: Yup.string().nullable().test(httpsUrlTest(eFinSuiteAdminMessages.VALID_URL)),
  description: Yup.string().nullable().max(WELCOME_LINK_DESCRIPTION_MAX_LENGTH, `Max ${WELCOME_LINK_DESCRIPTION_MAX_LENGTH} characters`),
  pointOfContact: Yup.string()
    .nullable()
    .test(
      'pointOfContact-validation',
      'Use only alphabets, space, and one comma. Examples: "useralias" or "useralias, anotheralias"',
      function (value) {
        if (value === null || value === undefined || value === '') {
          return true;
        }
        return /^[a-zA-Z]+(\s*,\s*[a-zA-Z]+)?$/.test(value);
      }
    )
});

export const linksAndTagsSchema = Yup.object().shape({
  hasNonProductionEnvironments: Yup.boolean().required(eFinSuiteAdminMessages.REQUIRED_FIELD),
  nonProductionEnvironments: Yup.array().when('hasNonProductionEnvironments', {
    is: true,
    then: Yup.array()
      .of(nonProductionEnvironmentSchema)
      .min(1, eFinSuiteAdminMessages.AT_LEAST_ONE_RECORD)
      .required(eFinSuiteAdminMessages.REQUIRED_FIELD),
    otherwise: Yup.array(nonProductionEnvironmentSchema)
  }),
  timeGranularity: OPTIONAL_MULTI_SELECT_DROPDOWN,
  keyDimension: OPTIONAL_MULTI_SELECT_DROPDOWN,
  lastEnhanced: OPTIONAL_MULTI_SELECT_DROPDOWN,
  process: OPTIONAL_MULTI_SELECT_DROPDOWN,
  scenario: OPTIONAL_MULTI_SELECT_DROPDOWN
});

export const ApplicationFormSchema = (applicationNames: string[], shouldSkipAppNameUniquenessValidation: boolean) =>
  Yup.object().shape({
    id: Yup.mixed().nullable(),
    applicationDetails: applicationDetailsSchema(applicationNames, shouldSkipAppNameUniquenessValidation),
    linksAndTags: linksAndTagsSchema
  });

/**
 * Run a Yup schema against the supplied values in `abortEarly:false` mode and
 * return the set of failed test names (e.g. `HTTPS_URL_TEST_NAME`).
 *
 * WHY this exists (SECURITY / UX decoupling):
 *   Formik's `validateForm()` returns a `FormikErrors` tree whose leaves are
 *   only the human-readable message strings — the underlying Yup `ValidationError`
 *   objects (which carry `.type` / `.path`) are discarded. That means the
 *   submit handler previously had to substring-match on `eFinSuiteAdminMessages.VALID_URL`
 *   to know whether "some URL field failed", which coupled the *display copy*
 *   (a localization concern) to *control flow* (a security concern). If a
 *   well-meaning contributor renamed the message to "Invalid URL", the branch
 *   would silently break and users would see the wrong step-error.
 *
 *   This helper avoids that coupling by re-running the schema and inspecting
 *   `error.inner[].type`, which is the Yup test name and is stable across
 *   copy/localization changes.
 *
 * Returns an empty Set when validation passes or when the thrown error is not
 * a Yup `ValidationError` (defensive — we never want this helper to itself
 * throw and take down the submit handler).
 */
export const collectFailedYupTests = async <T>(schema: Yup.AnySchema, values: T): Promise<Set<string>> => {
  try {
    await schema.validate(values, { abortEarly: false });
    return new Set<string>();
  } catch (err) {
    // Only ValidationError carries `.inner`; anything else is a programming
    // error and we prefer to surface it as "no known test failed" rather than
    // crash the submit path.
    if (!(err instanceof Yup.ValidationError)) return new Set<string>();
    const names = new Set<string>();
    // `err.inner` is populated when `abortEarly:false`; each entry represents
    // one failed test. `.type` is the test name we registered (see httpsUrlTest).
    for (const inner of err.inner) {
      if (inner.type) names.add(inner.type);
    }
    return names;
  }
};
